import { useEffect, useRef, useState, useImperativeHandle, forwardRef } from "react";
import { 
  createChart, 
  IChartApi, 
  ISeriesApi, 
  CandlestickData, 
  UTCTimestamp, 
  HistogramData,
} from "lightweight-charts";
import { fromSatoshi } from "../../sdk/utils/math";

export interface KlineChartProps {
  initialData?: CandlestickData[]; // Optional historical data
  symbol?: string; // e.g., "BTC/USDC"
  height?: number; // Chart height in pixels
  showVolume?: boolean; // Whether to show volume histogram
}

export interface KlineChartRef {
  updateChart: (kline: KlineUpdate) => void;
}

export interface KlineUpdate {
  open_time: number; // Unix timestamp in milliseconds
  close_time?: number; // Unix timestamp in milliseconds
  open: string | number; // Price in satoshi format (string) or decimal (number)
  high: string | number; // Price in satoshi format (string) or decimal (number)
  low: string | number; // Price in satoshi format (string) or decimal (number)
  close: string | number; // Price in satoshi format (string) or decimal (number)
  volume?: string | number; // Volume in satoshi format (string) or decimal (number)
}

const KlineChart = forwardRef<KlineChartRef, KlineChartProps>(
  ({ initialData = [], symbol = "BTC/USDC", height = 500, showVolume = true }, ref) => {
    const chartContainerRef = useRef<HTMLDivElement>(null);
    const chartRef = useRef<IChartApi | null>(null);
    const candlestickSeriesRef = useRef<any>(null);
    const volumeSeriesRef = useRef<any>(null);
    const [currentPrice, setCurrentPrice] = useState<number | null>(null);

    // Convert satoshi string to decimal number
    const parsePrice = (price: string | number): number => {
      if (typeof price === "number") return price;
      return fromSatoshi(price);
    };

    // Convert kline update to chart data format
    const convertKlineToBar = (kline: KlineUpdate): CandlestickData => {
      const time = Math.floor((kline.open_time || kline.close_time || Date.now()) / 1000) as UTCTimestamp;
      return {
        time,
        open: parsePrice(kline.open),
        high: parsePrice(kline.high),
        low: parsePrice(kline.low),
        close: parsePrice(kline.close),
      };
    };

    // Convert kline update to volume data format
    const convertKlineToVolume = (kline: KlineUpdate): HistogramData | null => {
      if (!kline.volume) return null;
      const time = Math.floor((kline.open_time || kline.close_time || Date.now()) / 1000) as UTCTimestamp;
      const volume = typeof kline.volume === "number" ? kline.volume : fromSatoshi(kline.volume);
      const close = parsePrice(kline.close);
      const open = parsePrice(kline.open);
      const isUp = close >= open;

      return {
        time,
        value: volume,
        color: isUp ? "#26a69a80" : "#ef535080", // Semi-transparent colors matching candlesticks
      };
    };

    // Function to update with new Kline bar (exposed via ref)
    const updateChart = (kline: KlineUpdate) => {
      const bar = convertKlineToBar(kline);
      const volumeBar = showVolume ? convertKlineToVolume(kline) : null;

      if (candlestickSeriesRef.current) {
        candlestickSeriesRef.current.update(bar);
        setCurrentPrice(bar.close);
      }

      if (showVolume && volumeSeriesRef.current && volumeBar) {
        volumeSeriesRef.current.update(volumeBar);
      }
    };

    // Expose update function via ref
    useImperativeHandle(ref, () => ({
      updateChart,
    }));

    useEffect(() => {
      if (!chartContainerRef.current) return;

      // Create chart
      const chart = createChart(chartContainerRef.current, {
        width: chartContainerRef.current.clientWidth,
        height: height,
        layout: {
          background: { color: "#1e1e1e" },
          textColor: "#d1d4dc",
        },
        grid: {
          vertLines: { color: "#2a2e39" },
          horzLines: { color: "#2a2e39" },
        },
        crosshair: {
          mode: 1, // Normal mode
        },
        rightPriceScale: {
          borderColor: "#2a2e39",
        },
        timeScale: {
          borderColor: "#2a2e39",
          timeVisible: true,
          secondsVisible: false,
        },
      });

      // Add candlestick series - using type assertion to work with v5 API
      const candlestickSeriesInstance = (chart as any).addSeries("Candlestick", {
        upColor: "#26a69a",
        downColor: "#ef5350",
        borderVisible: false,
        wickUpColor: "#26a69a",
        wickDownColor: "#ef5350",
      });

      // Add volume histogram if enabled
      let volumeSeries: any = null;
      if (showVolume) {
        volumeSeries = (chart as any).addSeries("Histogram", {
          color: "#26a69a80",
          priceFormat: {
            type: "volume",
          },
          priceScaleId: "",
        });
      }

      // Load initial/historical data if any
      if (initialData.length > 0) {
        candlestickSeriesInstance.setData(initialData);
        if (currentPrice === null && initialData.length > 0) {
          setCurrentPrice(initialData[initialData.length - 1].close);
        }
      }

      chartRef.current = chart;
      candlestickSeriesRef.current = candlestickSeriesInstance;
      volumeSeriesRef.current = volumeSeries;

      // Handle resize
      const handleResize = () => {
        if (chartContainerRef.current && chartRef.current) {
          chartRef.current.applyOptions({ width: chartContainerRef.current.clientWidth });
        }
      };
      window.addEventListener("resize", handleResize);

      return () => {
        window.removeEventListener("resize", handleResize);
        chart.remove();
      };
    }, [height, showVolume]); // Re-create chart if height or showVolume changes

    // Update initial data when it changes (but don't recreate chart)
    useEffect(() => {
      if (candlestickSeriesRef.current && initialData.length > 0) {
        candlestickSeriesRef.current.setData(initialData);
        if (initialData.length > 0) {
          setCurrentPrice(initialData[initialData.length - 1].close);
        }
      }
    }, [initialData]);

    return (
      <div style={{ position: "relative", width: "100%" }}>
        <div
          style={{
            marginBottom: "0.5rem",
            fontSize: "1.2rem",
            color: "#fff",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <span>
            {symbol} Live Kline Chart
            {currentPrice !== null && ` - Current: $${currentPrice.toFixed(2)}`}
          </span>
        </div>
        <div ref={chartContainerRef} style={{ position: "relative", width: "100%" }} />
      </div>
    );
  }
);

KlineChart.displayName = "KlineChart";

export default KlineChart;
